import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { useAuth } from "./AuthContext";
import { isStaff } from "../utils/roles";
import { supportChatEnabled } from "../utils/features";

export interface ChatRoom {
    id: number;
    customerUid: string;
    customerName: string | null;
    customerEmail: string | null;
    status: "open" | "closed";
    assignedStaff: string | null;
    aiMode: boolean;
    escalated: boolean;
    escalationReason: string | null;
    escalatedAt: string | null;
    lastMessageAt: string | null;
    lastMessage: string | null;
    unreadForStaff: number;
    unreadForUser: number;
    createdAt: string;
    updatedAt: string;
}

export type MessageType = "text" | "image" | "video" | "audio" | "document";

export interface ChatMessage {
    id: number;
    roomId: number;
    senderUid: string;
    senderRole: "customer" | "staff" | "bot";
    senderName: string | null;
    body: string;
    messageType: MessageType;
    attachmentKey: string | null;
    attachmentName: string | null;
    attachmentSize: number | null;
    attachmentMime: string | null;
    attachmentUrl: string | null;
    metadata: Record<string, any> | null;
    readAt: string | null;
    createdAt: string;
}

export interface BookingOption {
    type: "booking";
    label: string;
    pnr: string;
    bookingId: string;
    status: string;
    replyText: string;
}

interface IncomingMessageEvent {
    roomId: number;
    body: string;
    senderName: string | null;
    senderRole: "customer" | "staff";
}

interface EscalationEvent {
    roomId: number;
    customerName: string | null;
    escalationReason: string | null;
}

interface ChatContextValue {
    connected: boolean;
    role: "customer" | "staff" | null;
    rawRole: string | null;
    superAdmin: boolean;
    myUid: string | null;
    socket: Socket | null;
    rooms: ChatRoom[];
    unreadTotal: number;
    aiProcessing: boolean;
    refreshRooms: () => Promise<void>;
    onIncomingMessage: (cb: (e: IncomingMessageEvent) => void) => () => void;
    onEscalation: (cb: (e: EscalationEvent) => void) => () => void;
}

const ChatContext = createContext<ChatContextValue>({
    connected: false,
    role: null,
    rawRole: null,
    superAdmin: false,
    myUid: null,
    socket: null,
    rooms: [],
    unreadTotal: 0,
    aiProcessing: false,
    refreshRooms: async () => {},
    onIncomingMessage: () => () => {},
    onEscalation: () => () => {},
});

function baseUrl(): string {
    return (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, "");
}

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, profile } = useAuth();
    const [connected, setConnected] = useState(false);
    const [rooms, setRooms] = useState<ChatRoom[]>([]);
    const [role, setRole] = useState<"customer" | "staff" | null>(null);
    const [rawRole, setRawRole] = useState<string | null>(null);
    const [superAdmin, setSuperAdmin] = useState(false);
    const [aiProcessing, setAiProcessing] = useState(false);
    const socketRef = useRef<Socket | null>(null);
    const listenersRef = useRef<Set<(e: IncomingMessageEvent) => void>>(new Set());
    const escalationListenersRef = useRef<Set<(e: EscalationEvent) => void>>(new Set());

    const derivedRole: "customer" | "staff" | null = user
        ? isStaff(profile) ? "staff" : "customer"
        : null;

    const refreshRooms = async () => {
        if (!user || !supportChatEnabled) return;
        try {
            const res = await fetch(`${baseUrl()}/chat/rooms`, { credentials: "include" });
            if (!res.ok) return;
            const data = await res.json();
            if (data?.rooms) setRooms(data.rooms);
            if (data?.role) setRole(data.role);
            if (data?.rawRole !== undefined) setRawRole(data.rawRole);
            if (data?.superAdmin !== undefined) setSuperAdmin(!!data.superAdmin);
        } catch { /* silent */ }
    };

    const onIncomingMessage = (cb: (e: IncomingMessageEvent) => void) => {
        listenersRef.current.add(cb);
        return () => { listenersRef.current.delete(cb); };
    };

    const onEscalation = (cb: (e: EscalationEvent) => void) => {
        escalationListenersRef.current.add(cb);
        return () => { escalationListenersRef.current.delete(cb); };
    };

    useEffect(() => {
        // With support chat off there is nothing on the other end: the server
        // does not start the socket at all, so connecting would retry forever
        // and fill the console on every page.
        if (!user || !supportChatEnabled) {
            socketRef.current?.disconnect();
            socketRef.current = null;
            setConnected(false);
            setRooms([]);
            setRole(null);
            return;
        }

        console.log("[chat] connecting to", baseUrl());
        const s = io(baseUrl(), {
            withCredentials: true,
            transports: ["polling", "websocket"],
            reconnection: true,
            reconnectionAttempts: Infinity,
        });
        socketRef.current = s;

        s.on("connect", () => {
            console.log("[chat] connected", s.id);
            setConnected(true);
        });
        s.on("disconnect", (reason) => {
            console.warn("[chat] disconnected:", reason);
            setConnected(false);
        });
        s.on("connect_error", (err) => {
            console.error("[chat] connect_error:", err.message);
            setConnected(false);
        });

        s.on("room_updated", (room: ChatRoom) => {
            setRooms((prev) => {
                const idx = prev.findIndex((r) => r.id === room.id);
                if (idx === -1) return [room, ...prev];
                const next = [...prev];
                next[idx] = room;
                return next;
            });
        });

        s.on("new_message", (msg: any) => {
            // Fan out to any subscribers (e.g. toast manager in SupportInbox)
            listenersRef.current.forEach((cb) => {
                try {
                    cb({
                        roomId: msg.roomId,
                        body: msg.body,
                        senderName: msg.senderName ?? null,
                        senderRole: msg.senderRole,
                    });
                } catch { /* ignore listener errors */ }
            });
        });

        s.on("room_escalated", (room: ChatRoom) => {
            // Fan out to escalation subscribers (e.g. ChatNotifier)
            escalationListenersRef.current.forEach((cb) => {
                try {
                    cb({
                        roomId: room.id,
                        customerName: room.customerName,
                        escalationReason: room.escalationReason,
                    });
                } catch { /* ignore listener errors */ }
            });
        });

        s.on("ai_processing", (payload: { roomId: number; processing: boolean }) => {
            setAiProcessing(payload.processing);
        });

        refreshRooms();

        return () => {
            s.disconnect();
            socketRef.current = null;
        };
    }, [user?.uid]);

    useEffect(() => {
        setRole(derivedRole);
    }, [derivedRole]);

    const unreadTotal = useMemo(() => {
        if (role === "staff") return rooms.reduce((n, r) => n + (r.unreadForStaff || 0), 0);
        if (role === "customer") return rooms.reduce((n, r) => n + (r.unreadForUser || 0), 0);
        return 0;
    }, [rooms, role]);

    const value: ChatContextValue = {
        connected,
        role,
        rawRole,
        superAdmin,
        myUid: user?.uid ?? null,
        socket: socketRef.current,
        rooms,
        unreadTotal,
        aiProcessing,
        refreshRooms,
        onIncomingMessage,
        onEscalation,
    };

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => useContext(ChatContext);
